import { HttpErrorResponse, HttpResponse } from "@angular/common/http";
import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { MatRadioChange } from "@angular/material/radio";
import { MatTabChangeEvent } from "@angular/material/tabs";
import * as moment from "moment";
import { Subject } from "rxjs";
import { debounceTime, distinctUntilChanged, takeUntil } from "rxjs/operators";
import { ErrorType } from "src/app/enums/error-type.enum";
import endpoints from "src/app/helpers/endpoints";
import { ApiService } from "src/app/services/api.service";
import { CommonService } from "src/app/services/common.service";
import { StorageService } from "src/app/services/storage.service";
import { environment } from "src/environments/environment";

@Component({
  selector: "fury-renew-agreement",
  templateUrl: "./renew-agreement.component.html",
  styleUrls: ["./renew-agreement.component.scss"],
})
export class RenewAgreementComponent implements OnInit {
  ngUnSubscribe = new Subject<void>();
  expiryYear: number = Number(new Date().getFullYear().toString().slice(2, 4));
  month = Number(new Date().getMonth() + 1);
  minData = new Date().toISOString().slice(0, 10);
  cardDetailsForm = new FormGroup({
    cardHoldarName: new FormControl("", [Validators.required]),
    cardNumber: new FormControl("", [Validators.required]),
    expiryMonth: new FormControl("", [
      Validators.required,
      Validators.max(12),
      Validators.min(1),
    ]),
    expiryYear: new FormControl("", [
      Validators.required,
      Validators.min(this.expiryYear),
    ]),
    cvv: new FormControl("", [Validators.required]),
    monthYear: new FormControl("", [Validators.required]),
  });
  bankDetailsForm = new FormGroup({
    routingNumber: new FormControl("", [
      Validators.required,
      Validators.minLength(9),
      Validators.maxLength(9),
    ]),
    accountNumber: new FormControl("", [
      Validators.required,
      Validators.minLength(4),
    ]),
    nameOnAccount: new FormControl("", [
      Validators.minLength(2),
      Validators.required,
    ]),
    bankName: new FormControl("", [Validators.required]),
    transactionNumber: new FormControl("", [Validators.required]),
  });
  cardType = "";
  oneTimePlan = true;
  agreementDetails: any;
  coverageTerms = [
    {
      value: "Monthly",
      label: "MONTHLY",
      installmentLimit: "monthly",
    },
    {
      value: "Yearly",
      label: "ANNUALLY",
      installmentLimit: "yearly",
    },
    {
      value: "2 Years",
      label: "2 YEARS",
      installmentLimit: "twoYears",
    },
    {
      value: "3 Years",
      label: "3 YEARS",
      installmentLimit: "threeYears",
    },
    {
      value: "4 Years",
      label: "4 YEARS",
      installmentLimit: "fourYears",
    },
    {
      value: "5 Years",
      label: "5 YEARS",
      installmentLimit: "fiveYears",
    },
  ];
  additionalChargedPrice = 0;
  isEditableAdditionalPriceField = false;
  agentId = this.storageService.getUser()?.userId;
  agreementCalculationData: any;
  installmentForm = new FormGroup({
    paymentType: new FormControl<any>(1, [Validators.required]),
    initialPaymentDate: new FormControl(new Date() || null, [
      Validators.required,
    ]),
    initialPaidAmount: new FormControl(0),
    installmentPaymentDate: new FormControl(),
  });
  installmentLimits: any;
  limitOfInstallment = 0;
  oneTimeInstallmentPaymentPrice: any;
  isInstallmentFormDisabled = true;
  upcomingPayments = [];
  paymentTypeEnum = {
    0: "credit-debit-card",
    1: "bank-payment",
  };
  paymentType = this.paymentTypeEnum[0];
  defaultCard: any;
  isLoading = false;
  assetsUrl = environment.assetsUrl + "images/";

  constructor(
    private commonService: CommonService,
    public dialogRef: MatDialogRef<RenewAgreementComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      agreementId: string;
      calculationType: string;
      userId: string;
    },
    public apiService: ApiService,
    private storageService: StorageService
  ) {}

  ngOnInit(): void {
    this.installmentForm.disable();
    this.getAgreementDetails();
    this.getInstallmentLimitSettings();
    this.cardDetailsForm
      .get("monthYear")
      ?.valueChanges.pipe(takeUntil(this.ngUnSubscribe))
      .subscribe((res: string) => {
        if (res) {
          if (res.length === 4) {
            this.cardDetailsForm.patchValue({
              expiryMonth: res.substring(0, 2),
              expiryYear: res.substring(2, 4),
            });
            if (
              this.cardDetailsForm.get("expiryYear").value ===
                this.expiryYear.toString() &&
              Number(this.cardDetailsForm.get("expiryMonth").value) < this.month
            ) {
              this.cardDetailsForm
                .get("expiryMonth")
                .setErrors({ notValid: true });
            }
          }
        }
      });
  }
  close() {
    this.dialogRef.close();
  }
  detectCardType(cardNumber: string) {
    this.cardType = this.commonService.getCardType(cardNumber);
  }
  togglePaymentType(isOneTimePlan: boolean) {
    this.oneTimePlan = isOneTimePlan;
  }
  getAgreementDetails() {
    this.apiService
      .get(
        endpoints.customerDashboard.getAgreementDetails(this.data.agreementId)
      )
      .pipe(takeUntil(this.ngUnSubscribe))
      .subscribe({
        next: (res: HttpResponse<any>) => {
          if (res.body) {
            this.agreementDetails = res.body?.agreement;
            let coverageTerm = this.coverageTerms.find(
              (item) => item.value === this.agreementDetails?.pricingOption
            );
            this.limitOfInstallment =
              this.installmentLimits[coverageTerm.installmentLimit];
            this.installmentForm.get("paymentType").setValue(1);
            this.renewAgreement(0);
            if (this.agreementDetails?.pricingOption === "Monthly")
              this.oneTimePlan = true;
          }
        },
        error: (err: HttpErrorResponse) => {
          this.apiService.alert(err.message, ErrorType.Error);
        },
      });
  }


  getInstallmentLimitSettings() {
    this.apiService
      .get(endpoints.checkoutSettings.getCheckoutSettings)
      .pipe(takeUntil(this.ngUnSubscribe))
      .subscribe({
        next: (res: HttpResponse<any>) => {
          if (res.body) {
            const data = res.body?.checkout_setting?.installmentLimits;
            this.installmentLimits = {
              yearly: data?.annuallyPlanInstallmentLimit,
              twoYears: data?.twoYearsPlanIInstallmentLimit,
              threeYears: data?.threeYearsPlanIInstallmentLimit,
              fourYears: data?.fourYearsPlanIInstallmentLimit,
              fiveYears: data?.fiveYearsPlanIInstallmentLimit,
            };
            // this.renewAgreement(0);
          }
        },
        error: (err: HttpErrorResponse) => {
          this.apiService.alert(err.message, ErrorType.Error);
        },
      });
  }
  repeatArray(count: number) {
    return Array(count).fill(0);
  }
  installmentText(index: number) {
    const textArray = [
      "",
      "One Payment",
      "Two Payments",
      "Three Payments",
      "Four Payments",
      "Five Payments",
      "Six Payments",
      "Seven Payments",
      "Eight Payments",
      "Nine Payments",
    ];
    if (index > 0 && index < 10) {
      return textArray[index];
    }
    return "";
  }

  getInstallmentCalculations() {
    let payload = Object.assign(this.installmentForm.value, {
      initialPaymentDate: moment(
        this.installmentForm.value.initialPaymentDate
      ).format("YYYY-MM-DD"),
      agentId: this.agentId,
      extraChargedPrice: this.agreementCalculationData?.subTotalAmount,
      additionalChargedPrice: this.agreementCalculationData?.additionalPrice,
      pricingOption: this.agreementCalculationData.pricingOption,
      paymentType: `${this.installmentForm.value.paymentType} ${
        this.installmentForm.value.paymentType > 1 ? "Payments" : "Payment"
      }`,
      initialPaidAmount:
        this.installmentForm.value.paymentType > 1
          ? this.installmentForm.value.initialPaidAmount || 0
          : this.oneTimeInstallmentPaymentPrice || 0,
      installmentPaymentDate: moment(
        this.installmentForm.value.installmentPaymentDate
      ).format("YYYY-MM-DD"),
    });
    this.apiService
      .post(
        endpoints.customerDashboard.getAgreementIntllmentCalculation(
          this.data.agreementId
        ),
        payload
      )
      .pipe(takeUntil(this.ngUnSubscribe))
      .subscribe({
        next: (res: HttpResponse<any>) => {
          if (res.body) {
            this.upcomingPayments = res.body?.upcomingPayments || [];
          }
        },
        error: (err: HttpErrorResponse) => {
          this.upcomingPayments = [];
        },
      });
  }
  toggleInstallmentDisabledForm(isDisabled: boolean) {
    if (isDisabled) {
      if (this.installmentForm.invalid) {
        this.installmentForm.markAllAsTouched();
        return;
      }
      this.isInstallmentFormDisabled = isDisabled;
      if (this.extractNumber(this.installmentForm.value.paymentType) > 1) this.getInstallmentCalculations();
      this.installmentForm.disable();
    } else {
      this.isInstallmentFormDisabled = isDisabled;
      this.installmentForm.enable();
    }
  }
  onTabChange(event: MatTabChangeEvent) {
    this.paymentType = this.paymentTypeEnum[event.index];
  }

  extractNumber(input) {
    if (typeof input === "number") {
      return input;
    }
    if (typeof input === "string") {
      const match = input.match(/^(\d+)/);
      return match ? parseInt(match[1], 10) : null;
    }
    return null;
  }

  getBankDetail() {
    if (this.bankDetailsForm.value.routingNumber.length !== 9) {
      this.bankDetailsForm.get("bankName").setValue("");
      return;
    }

    this.apiService
      .get(
        endpoints.customerDashboard.checkout.getBankDetail(
          this.bankDetailsForm.value.routingNumber
        )
      )
      .pipe(debounceTime(1000))
      .pipe(distinctUntilChanged())
      .subscribe(
        (res: any) => {
          if (res?.body?.bankName) {
            this.bankDetailsForm.get("bankName").setValue(res?.body?.bankName);
            return;
          }
          this.bankDetailsForm.get("bankName").setValue("");
        },
        (err) => {
          this.bankDetailsForm.get("bankName").setValue("");
          this.apiService.alert(err.error.message, ErrorType.Error);
        }
      );
  }
  renewAgreement(saveData: number, paymentPayload?: any) {
    if(saveData === 1) this.isLoading = true;
    let payload: any = this.createPayload(saveData);
    if (paymentPayload) {
      payload = { ...payload, ...paymentPayload };
    }

    this.apiService
      .patch(
        endpoints.customerDashboard.renewAgreement(
          this.data.agreementId,
          saveData
        ),
        payload
      )
      .pipe(takeUntil(this.ngUnSubscribe))
      .subscribe({
        next: (res: HttpResponse<any>) => {
          if (res.body) {
            this.agreementCalculationData = res.body;
            this.oneTimeInstallmentPaymentPrice =
              this.agreementCalculationData?.subTotalAmount;
            if(saveData === 1) {
              this.isLoading = false;
              this.dialogRef.close(res.body);
              this.apiService.alert(res.body.message, ErrorType.Success);
            }
          }
        },
        error: (err: HttpErrorResponse) => {
          this.apiService.alert(err.message, ErrorType.Error);
          if(saveData === 1) this.isLoading = false;
        },
      });
  }
  get installmentMinDate() {
    let installmentData = this.installmentForm.get("initialPaymentDate").value;
    let minDate = moment(installmentData)
      .add(1, "days")
      .toISOString()
      .split("T")[0];
    return minDate;
  }
  radioChange(event: MatRadioChange) {
    this.upcomingPayments = [];
    const installmentPaymentDateControl = this.installmentForm.get(
      "installmentPaymentDate"
    );
    if (event.value > 1) {
      installmentPaymentDateControl.setValidators(Validators.required);
      installmentPaymentDateControl.updateValueAndValidity();
    } else {
      installmentPaymentDateControl.setValue(null);
      installmentPaymentDateControl.clearValidators();
      installmentPaymentDateControl.updateValueAndValidity();
    }
  }
  createPayload(type: number) {
    let payload: any = {};
    if (type === 0) {
      payload = {
        paymentType:
          this.agreementDetails?.pricingOption === "Monthly"
            ? "Monthly"
            : "Full Payment",
        agentId: this.agentId,
      };
    }
    if (type === 1) {
      payload = {
        agentId: this.agentId,
        pricingOption: this.agreementDetails?.pricingOption,
        // copayFee: this.agreementCalculationData?.reflectedCopayAmount,
      };
      if (!this.oneTimePlan) {
        payload.initialPaymentDate = moment(
          this.installmentForm.value.initialPaymentDate
        ).format("YYYY-MM-DD");
        payload.initialPaidAmount =
          this.extractNumber(this.installmentForm.value.paymentType) > 1
            ? this.installmentForm.value.initialPaidAmount || 0
            : this.oneTimeInstallmentPaymentPrice || 0;
        payload.paymentType = `${this.extractNumber(
          this.installmentForm.value.paymentType
        )} ${
          this.extractNumber(this.installmentForm.value.paymentType) === 1
            ? "Payment"
            : "Payments"
        }`;
        if (this.extractNumber(this.installmentForm.value.paymentType) > 1) {
          payload.installmentPaymentDate = moment(
            this.installmentForm.value.installmentPaymentDate
          ).format("YYYY-MM-DD");
        }
      } else {
        payload.paymentType =
          this.agreementDetails?.pricingOption === "Monthly"
            ? "Monthly"
            : "Full Payment";
      }
    }
    return payload;
  }
  checkPaymentForm() {
    let payload: any;
    if (this.paymentType === this.paymentTypeEnum[0]) {
      if (this.cardDetailsForm.invalid) {
        this.cardDetailsForm.markAllAsTouched();
        return;
      }
      payload = {
        paymentDetail: {
          cardNumber: this.cardDetailsForm.value.cardNumber,
          cardHoldarName: this.cardDetailsForm.value.cardHoldarName,
          cvv: this.cardDetailsForm.value.cvv,
          expiryMonth: this.cardDetailsForm.value.expiryMonth,
          expiryYear: `20${this.cardDetailsForm.value.expiryYear}`,
        },
        paymentMethod: "credit-debit-card",
      };
    }
    if (this.paymentType === this.paymentTypeEnum[1]) {
      if (this.bankDetailsForm.invalid) {
        this.bankDetailsForm.markAllAsTouched();
        return;
      }
      payload = {
        bankDetail: {
          bankName: this.bankDetailsForm.value.bankName,
          routingNumber: this.bankDetailsForm.value.routingNumber,
          accountNumber: this.bankDetailsForm.value.accountNumber,
          nameOnAccount: this.bankDetailsForm.value.nameOnAccount,
        },
        transactionNumber: this.bankDetailsForm.value.transactionNumber,
        paymentMethod: "bank-payment",
      };
    }
    this.renewAgreement(1, payload);
  }

  ngOnDestroy() {
    this.ngUnSubscribe.next();
    this.ngUnSubscribe.complete();
  }
}
